"use client";

import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import type { BillingHistory as BillingHistoryType } from "../types/types";
import { useAuth } from "../contexts/AuthContext";
import { loadStripe } from "@stripe/stripe-js";
import { useToast } from "@chakra-ui/react";
import { ConfirmationDialog } from "../components/ui/ConfirmationDialog";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  HStack,
  VStack,
  Image,
  Card,
  CardBody,
  CardHeader,
  Alert,
  AlertIcon,
  Spinner,
  Center,
  Link as ChakraLink,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  TableContainer,
} from "@chakra-ui/react";
import { FiArrowLeft } from "react-icons/fi";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

export default function BillingHistoryPage() {
  const {
    user,
    profile,
    loading: authLoading,
    refreshProfile,
    session,
  } = useAuth();
  const [billingHistory, setBillingHistory] = useState<BillingHistoryType[]>(
    []
  );
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [creditsToBuy, setCreditsToBuy] = useState(10);
  const [selectedPlan, setSelectedPlan] = useState<"basic" | "premium">(
    "basic"
  );

  const toast = useToast();
  const [processedSessionId, setProcessedSessionId] = useState<string | null>(
    null
  );
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Effect for handling Stripe redirect and showing toasts
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const status = searchParams.get("status");

    // Only process if we have both sessionId and status, and haven't processed this session yet
    if (sessionId && status && sessionId !== processedSessionId) {
      setProcessedSessionId(sessionId);

      const handleRedirect = async () => {
        if (status === "success" || status === "success_credits") {
          try {
            // Add a small delay to ensure webhook has processed
            await new Promise((resolve) => setTimeout(resolve, 2000));
            await refreshProfile();
            toast({
              title: "Payment Successful!",
              description:
                status === "success"
                  ? "Subscription updated successfully!"
                  : "Credits purchased successfully!",
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          } catch (e) {
            console.error("Error refreshing profile after payment:", e);
            toast({
              title: "Update Pending",
              description:
                "Payment was successful, but profile update might be delayed. Please refresh shortly.",
              status: "warning",
              duration: 5000,
              isClosable: true,
            });
          }
        } else if (status === "cancel" || status === "cancel_credits") {
          toast({
            title: "Payment Cancelled",
            description: "The payment process was cancelled.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }

        // Clear the URL parameters without causing a reload
        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      };

      handleRedirect();
    }
  }, [searchParams, processedSessionId, refreshProfile, toast]);

  // Effect for fetching billing history - only run when user changes or on mount
  useEffect(() => {
    if (authLoading || !user) {
      setPageLoading(false);
      return;
    }

    const fetchBillingHistoryData = async () => {
      setPageLoading(true);
      setError(null);

      try {
        const { data, error: historyError } = await supabase
          .from("billing_history")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });

        if (historyError) throw historyError;
        setBillingHistory(data || []);
      } catch (err: any) {
        setError(err.message);
        console.error("Error fetching billing history:", err);
      } finally {
        setPageLoading(false);
      }
    };

    fetchBillingHistoryData();
  }, [user, authLoading]); // Only depend on user and authLoading

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const handleApiCall = async (
    endpoint: string,
    body: any,
    successMessage: string
  ) => {
    setError(null);
    if (!session?.access_token) {
      setError("Authentication token not found. Please log in again.");
      return null;
    }

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        console.error(`[API Call] Error response:`, data);
        throw new Error(
          data.error ||
            `Failed to call ${endpoint} - Status: ${response.status}`
        );
      }
      return data;
    } catch (err: any) {
      console.error(`Error in handleApiCall for ${endpoint}:`, err);
      setError(err.message);
      return null;
    }
  };

  const handleSubscribe = async (
    planType: "basic" | "premium",
    duration: "monthly" | "biannual" | "yearly" = "monthly"
  ) => {
    if (!profile || !user || !session) {
      setError("User or session not available for subscription.");
      return;
    }

    setIsProcessing(true);
    const stripe = await stripePromise;
    if (!stripe) {
      setError("Stripe.js not loaded.");
      setIsProcessing(false);
      return;
    }

    let priceId;
    if (duration === "monthly") {
      priceId =
        planType === "basic"
          ? process.env.REACT_APP_STRIPE_PRICE_ID_STANDARD_MONTHLY_SUBSCRIPTION
          : process.env.REACT_APP_STRIPE_PRICE_ID_PREMIUM_MONTHLY_SUBSCRIPTION;
    } else if (duration === "biannual") {
      priceId =
        planType === "basic"
          ? process.env.REACT_APP_STRIPE_PRICE_ID_STANDARD_BIANNUAL_SUBSCRIPTION
          : process.env.REACT_APP_STRIPE_PRICE_ID_PREMIUM_BIANNUAL_SUBSCRIPTION;
    } else if (duration === "yearly") {
      priceId =
        planType === "basic"
          ? process.env.REACT_APP_STRIPE_PRICE_ID_STANDARD_YEARLY_SUBSCRIPTION
          : process.env.REACT_APP_STRIPE_PRICE_ID_PREMIUM_YEARLY_SUBSCRIPTION;
    }

    const serverUrl =
      process.env.REACT_APP_SERVER_URL || "http://localhost:8000";
    const endpoint = `${serverUrl}/create-checkout-session-subscription`;

    const sessionData = await handleApiCall(
      endpoint,
      {
        priceId: priceId,
        planType: planType,
      },
      "Redirecting to subscription..."
    );

    if (sessionData && sessionData.id) {
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: sessionData.id,
      });
      if (stripeError) {
        setError(stripeError.message || "Failed to redirect to Stripe.");
        setIsProcessing(false);
      }
    } else {
      setIsProcessing(false);
    }
  };

  const handleBuyCredits = async () => {
    if (!profile || !user || !session) {
      setError("User or session not available for buying credits.");
      return;
    }
    if (creditsToBuy <= 0) {
      setError("Please enter a valid number of credits to buy.");
      return;
    }

    setIsProcessing(true);
    const stripe = await stripePromise;
    if (!stripe) {
      setError("Stripe.js not loaded.");
      setIsProcessing(false);
      return;
    }

    const priceId = process.env.REACT_APP_STRIPE_PRICE_ID_ADDITIONAL_CREDITS;

    const serverUrl =
      process.env.REACT_APP_SERVER_URL || "http://localhost:8000";
    const endpoint = `${serverUrl}/create-checkout-session-credits`;

    const sessionData = await handleApiCall(
      endpoint,
      {
        priceId: priceId,
        quantity: creditsToBuy,
      },
      "Redirecting to purchase credits..."
    );

    if (sessionData && sessionData.id) {
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: sessionData.id,
      });
      if (stripeError) {
        setError(stripeError.message || "Failed to redirect to Stripe.");
        setIsProcessing(false);
      }
    } else {
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = () => {
    if (!session) {
      setError("Session unavailable. Please log in again.");
      return;
    }
    setShowCancelDialog(true);
  };

  const confirmCancelSubscription = async () => {
    if (!profile?.stripe_subscription_id) {
      toast({
        title: "Subscription not found",
        description:
          "We couldn't locate an active Stripe subscription. Please contact support.",
        status: "error",
      });
      return;
    }

    setIsProcessing(true);
    setShowCancelDialog(false);
    setError(null);

    try {
      const result = await handleApiCall(
        `${process.env.REACT_APP_SERVER_URL || ""}/cancel-subscription`,
        {},
        "Cancelling subscription..."
      );

      if (result) {
        await refreshProfile();
        toast({
          title: "Subscription Cancelled",
          description:
            "Your subscription has been cancelled and will end at the end of your current billing period.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err: any) {
      setError(err.message || "Failed to cancel subscription");
      toast({
        title: "Cancellation Failed",
        description:
          "There was an error cancelling your subscription. Please try again or contact support.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Helper functions for subscription logic
  const hasActiveSubscription = profile?.subscription_status === "active";
  const hasCanceledButActiveSubscription =
    profile?.subscription_status === "canceled" &&
    profile?.subscription_period_end &&
    new Date(profile.subscription_period_end) > new Date();

  const canPurchaseCredits =
    hasActiveSubscription || hasCanceledButActiveSubscription;

  const getCreditPrice = () => {
    return 10;
  };

  const getPlanDetails = (planType: "basic" | "premium") => {
    if (planType === "basic") {
      return {
        name: "Standard Plan",
        price: "$300",
        credits: 15,
        creditPrice: "$10",
        description: "Perfect for small practices",
      };
    } else {
      return {
        name: "Premium Plan",
        price: "$500",
        credits: 25,
        creditPrice: "$10",
        description: "Best value for larger practices",
      };
    }
  };

  if (authLoading || pageLoading) {
    return (
      <Center minH="100vh" flexDirection="column">
        <Spinner size="xl" color="primary.500" mb={4} />
        <Text>Loading billing information...</Text>
      </Center>
    );
  }

  if (error) {
    return (
      <Box p={8} textAlign="center">
        <Alert status="error" borderRadius="md" textColor={"red.600"}>
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box p={8} textAlign="center">
        <Alert status="warning" borderRadius="md" textColor={"orange.600"}>
          <AlertIcon />
          Please log in to view billing information.
        </Alert>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box p={8} textAlign="center">
        <Alert status="error" borderRadius="md" textColor={"red.600"}>
          <AlertIcon />
          User profile data is missing. Cannot display billing information.
        </Alert>
      </Box>
    );
  }

  const totalRemainingCredits =
    (profile.subscription_remaining_credits || 0) +
    (profile.purchased_remaining_credits || 0);

  return (
    <Box minH="100vh" bg="gray.50">
      <Box
        bg="rgb(253, 251, 252)"
        shadow="sm"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Container maxW="7xl">
          <Flex h={16} align="center" justify="space-between">
            <HStack spacing={4}>
              <Image
                src="/main-logo.png"
                alt="RadiusDocs AI"
                height="50px"
                width="auto"
                objectFit="contain"
              />
            </HStack>

            <HStack spacing={6}>
              <ChakraLink
                as={Link}
                to="/dashboard"
                color="gray.600"
                fontWeight="medium"
              >
                Dashboard
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/document-history"
                color="gray.600"
                fontWeight="medium"
              >
                Documents
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/templates"
                color="gray.600"
                fontWeight="medium"
              >
                Templates
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/billing"
                color="primary.600"
                fontWeight="medium"
              >
                Billing
              </ChakraLink>
            </HStack>

            <Button
              onClick={() => navigate("/dashboard")}
              variant="ghost"
              leftIcon={<FiArrowLeft />}
              colorScheme="primary"
            >
              Back
            </Button>
          </Flex>
        </Container>
      </Box>

      <Container maxW="7xl" py={8}>
        <VStack spacing={6} align="stretch">
          <Flex justify="space-between" align="center">
            <Heading size="xl" color="gray.800">
              Billing & Subscription
            </Heading>
          </Flex>
          <SimpleGrid
            columns={{
              base: 1,
              md: profile.subscription_status === "active" ? 5 : 4,
            }}
            spacing={4}
          >
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="primary.600">
                  {totalRemainingCredits}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Total Credits Remaining
                </Text>
              </CardBody>
            </Card>
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="primary.600">
                  {profile.subscription_remaining_credits || 0}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Subscription Credits
                  {profile.subscription_status === "canceled" &&
                    profile.subscription_period_end && (
                      <Text fontSize="xs" color="orange.600" mt={1}>
                        Expires:{" "}
                        {new Date(
                          profile.subscription_period_end
                        ).toLocaleDateString()}
                      </Text>
                    )}
                </Text>
              </CardBody>
            </Card>
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="primary.600">
                  {profile.purchased_remaining_credits || 0}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Purchased Credits
                  <Text fontSize="xs" color="green.600" mt={1}>
                    Never expire
                  </Text>
                </Text>
              </CardBody>
            </Card>
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="primary.600">
                  {profile.subscription_status
                    ? profile.subscription_status.charAt(0).toUpperCase() +
                      profile.subscription_status.slice(1)
                    : "Not Subscribed"}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Subscription Status
                </Text>
              </CardBody>
            </Card>
            {profile.subscription_period_end && (
              <Card>
                <CardBody textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="primary.600">
                    {new Date(
                      profile.subscription_period_end
                    ).toLocaleDateString()}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {profile.subscription_status === "canceled"
                      ? "Ends On"
                      : "Renews On"}
                  </Text>
                </CardBody>
              </Card>
            )}
          </SimpleGrid>

          {/* Current Plan Display */}
          {hasActiveSubscription && (
            <Card>
              <CardHeader>
                <Heading size="md">Current Plan</Heading>
              </CardHeader>
              <CardBody>
                <Flex justify="space-between" align="center">
                  <Box>
                    <Heading size="sm" mb={1}>
                      {
                        getPlanDetails(profile.subscription_plan || "basic")
                          .name
                      }
                    </Heading>
                    <Text color="gray.600">
                      {
                        getPlanDetails(profile.subscription_plan || "basic")
                          .credits
                      }{" "}
                      credits per month • Additional credits at{" "}
                      {
                        getPlanDetails(profile.subscription_plan || "basic")
                          .creditPrice
                      }{" "}
                      each
                    </Text>
                  </Box>
                  <Badge colorScheme="green" size="lg">
                    Active
                  </Badge>
                </Flex>
              </CardBody>
            </Card>
          )}

          {/* Subscription Plans */}
          {!hasActiveSubscription && (
            <VStack spacing={6} align="stretch" mt={8}>
              <Heading size="md">Choose Your Plan</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {/* Standard Plan */}
                <Card
                  borderWidth={2}
                  borderColor={
                    selectedPlan === "basic" ? "primary.500" : "gray.200"
                  }
                >
                  <CardHeader>
                    <Flex justify="space-between" align="center">
                      <Box>
                        <Heading size="md">Standard Plan</Heading>
                        <Text color="gray.600">
                          Perfect for small practices
                        </Text>
                      </Box>
                      <Text
                        fontSize="2xl"
                        fontWeight="bold"
                        color="primary.600"
                      >
                        $300
                        <Text fontSize="sm" color="gray.600">
                          /month
                        </Text>
                      </Text>
                    </Flex>
                  </CardHeader>
                  <CardBody>
                    <VStack align="start" spacing={2} mb={4}>
                      <Text>✓ 15 credits per month</Text>
                      <Text>✓ Additional credits at $10 each</Text>
                      <Text>✓ Email support</Text>
                      <Text>✓ All document types</Text>
                    </VStack>
                    <Button
                      onClick={() => handleSubscribe("basic")}
                      colorScheme="primary"
                      size="md"
                      width="full"
                      isLoading={isProcessing}
                      loadingText="Processing..."
                    >
                      Subscribe to Standard
                    </Button>
                  </CardBody>
                </Card>

                {/* Premium Plan */}
                <Card
                  borderWidth={2}
                  borderColor={
                    selectedPlan === "premium" ? "primary.500" : "gray.200"
                  }
                >
                  <CardHeader>
                    <Flex justify="space-between" align="center">
                      <Box>
                        <Heading size="md">Premium Plan</Heading>
                        <Text color="gray.600">
                          Best value for larger practices
                        </Text>
                      </Box>
                      <Text
                        fontSize="2xl"
                        fontWeight="bold"
                        color="primary.600"
                      >
                        $500
                        <Text fontSize="sm" color="gray.600">
                          /month
                        </Text>
                      </Text>
                    </Flex>
                  </CardHeader>
                  <CardBody>
                    <VStack align="start" spacing={2} mb={4}>
                      <Text>✓ 25 credits per month</Text>
                      <Text>✓ Additional credits at $10 each</Text>
                      <Text>✓ Priority support</Text>
                      <Text>✓ All document types</Text>
                    </VStack>
                    <Button
                      onClick={() => handleSubscribe("premium")}
                      colorScheme="primary"
                      size="md"
                      width="full"
                      isLoading={isProcessing}
                      loadingText="Processing..."
                    >
                      Subscribe to Premium
                    </Button>
                  </CardBody>
                </Card>
              </SimpleGrid>

              {/* 6-Month Plans */}
              <VStack spacing={4} align="stretch" mt={6}>
                <Heading size="sm" color="gray.600">
                  Save with Longer Commitments
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {/* 6-Month Plans */}
                  <Card borderWidth={1} borderColor="green.200" bg="green.50">
                    <CardHeader pb={2}>
                      <Flex justify="space-between" align="center">
                        <Heading size="sm" color="green.700">
                          6-Month Plans
                        </Heading>
                        <Badge colorScheme="green">Save 10%</Badge>
                      </Flex>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack spacing={2} align="start">
                        <Flex justify="space-between" w="full">
                          <Text fontSize="sm">Standard (90 credits):</Text>
                          <Text fontSize="sm" fontWeight="bold">
                            $1,620{" "}
                            <Text
                              as="span"
                              textDecoration="line-through"
                              color="gray.500"
                            >
                              $1,800
                            </Text>
                          </Text>
                        </Flex>
                        <Flex justify="space-between" w="full">
                          <Text fontSize="sm">Premium (150 credits):</Text>
                          <Text fontSize="sm" fontWeight="bold">
                            $2,700{" "}
                            <Text
                              as="span"
                              textDecoration="line-through"
                              color="gray.500"
                            >
                              $3,000
                            </Text>
                          </Text>
                        </Flex>
                        <HStack spacing={2} mt={2}>
                          <Button
                            size="sm"
                            colorScheme="green"
                            variant="outline"
                            onClick={() => handleSubscribe("basic", "biannual")}
                            isLoading={isProcessing}
                          >
                            Standard 6mo
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="green"
                            variant="outline"
                            onClick={() =>
                              handleSubscribe("premium", "biannual")
                            }
                            isLoading={isProcessing}
                          >
                            Premium 6mo
                          </Button>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Annual Plans */}
                  <Card
                    borderWidth={1}
                    borderColor="primary.200"
                    bg="primary.50"
                  >
                    <CardHeader pb={2}>
                      <Flex justify="space-between" align="center">
                        <Heading size="sm" color="primary.700">
                          Annual Plans
                        </Heading>
                        <Badge colorScheme="primary">Save 20%</Badge>
                      </Flex>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack spacing={2} align="start">
                        <Flex justify="space-between" w="full">
                          <Text fontSize="sm">Standard (180 credits):</Text>
                          <Text fontSize="sm" fontWeight="bold">
                            $2,880{" "}
                            <Text
                              as="span"
                              textDecoration="line-through"
                              color="gray.500"
                            >
                              $3,600
                            </Text>
                          </Text>
                        </Flex>
                        <Flex justify="space-between" w="full">
                          <Text fontSize="sm">Premium (300 credits):</Text>
                          <Text fontSize="sm" fontWeight="bold">
                            $4,800{" "}
                            <Text
                              as="span"
                              textDecoration="line-through"
                              color="gray.500"
                            >
                              $6,000
                            </Text>
                          </Text>
                        </Flex>
                        <HStack spacing={2} mt={2}>
                          <Button
                            size="sm"
                            colorScheme="primary"
                            variant="outline"
                            onClick={() => handleSubscribe("basic", "yearly")}
                            isLoading={isProcessing}
                          >
                            Standard 1yr
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="primary"
                            variant="outline"
                            onClick={() => handleSubscribe("premium", "yearly")}
                            isLoading={isProcessing}
                          >
                            Premium 1yr
                          </Button>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>
                </SimpleGrid>
              </VStack>
            </VStack>
          )}

          {/* Subscription Management */}
          {hasActiveSubscription && (
            <Card>
              <CardHeader>
                <Heading size="md">Subscription Management</Heading>
              </CardHeader>
              <CardBody>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                  align={{ base: "stretch", md: "center" }}
                >
                  <Box mb={{ base: 4, md: 0 }}>
                    <Heading size="sm" mb={1}>
                      Cancel Subscription
                    </Heading>
                    <Text color="gray.600">
                      Your subscription will remain active until the end of your
                      current billing period
                      {profile.subscription_period_end && (
                        <>
                          {" "}
                          (
                          {new Date(
                            profile.subscription_period_end
                          ).toLocaleDateString()}
                          )
                        </>
                      )}
                    </Text>
                  </Box>
                  <Button
                    onClick={handleCancelSubscription}
                    colorScheme="red"
                    variant="ghost"
                    size="md"
                    isLoading={isProcessing}
                    loadingText="Processing..."
                  >
                    Cancel Subscription
                  </Button>
                </Flex>
              </CardBody>
            </Card>
          )}

          {/* Canceled Subscription Status */}
          {profile.subscription_status === "canceled" && (
            <Card>
              <CardHeader>
                <Heading size="md">Subscription Status</Heading>
              </CardHeader>
              <CardBody>
                <Box>
                  <Heading size="sm" mb={1}>
                    Subscription Cancelled
                  </Heading>
                  <Text color="gray.600">
                    Your subscription has been cancelled and will end on{" "}
                    {profile.subscription_period_end
                      ? new Date(
                          profile.subscription_period_end
                        ).toLocaleDateString()
                      : "the end of your billing period"}
                  </Text>
                </Box>
              </CardBody>
            </Card>
          )}

          {/* Buy Additional Credits - Only show if user has active subscription */}
          {canPurchaseCredits && (
            <Card mt={6}>
              <CardHeader>
                <Heading size="md">Buy Additional Credits</Heading>
              </CardHeader>
              <CardBody>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                  align={{ base: "stretch", md: "center" }}
                >
                  <Box mb={{ base: 4, md: 0 }}>
                    <Heading size="sm" mb={1}>
                      Purchase Credits
                    </Heading>
                    <Text color="gray.600">
                      Buy additional credits at ${getCreditPrice()} per credit
                      {profile.subscription_plan && (
                        <Text fontSize="sm" color="primary.600" mt={1}>
                          {profile.subscription_plan === "premium"
                            ? "Premium"
                            : "Standard"}{" "}
                          plan pricing
                        </Text>
                      )}
                    </Text>
                  </Box>
                  <HStack spacing={4}>
                    <FormControl w={{ base: "full", md: "150px" }}>
                      <FormLabel htmlFor="credits-input" fontSize="sm">
                        Credits
                      </FormLabel>
                      <NumberInput
                        id="credits-input"
                        min={1}
                        value={creditsToBuy}
                        onChange={(valueString) =>
                          setCreditsToBuy(Number.parseInt(valueString))
                        }
                        isDisabled={isProcessing}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper color="gray.300" />
                          <NumberDecrementStepper color="gray.300" />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <Button
                      onClick={handleBuyCredits}
                      colorScheme="primary"
                      isDisabled={creditsToBuy <= 0 || isProcessing}
                      isLoading={isProcessing}
                      loadingText="Processing..."
                      marginTop={8}
                    >
                      Buy
                      <Badge ml={2} colorScheme="green">
                        ${(creditsToBuy * getCreditPrice()).toFixed(2)}
                      </Badge>
                    </Button>
                  </HStack>
                </Flex>
              </CardBody>
            </Card>
          )}

          {/* Message for users without active subscription */}
          {!canPurchaseCredits && (
            <Card mt={6} borderColor="orange.200" borderWidth={2}>
              <CardBody textAlign="center" py={8}>
                <Text color="orange.600" fontWeight="medium" mb={2}>
                  Additional Credits Unavailable
                </Text>
                <Text color="gray.600">
                  You need an active subscription to purchase additional
                  credits. Please subscribe to a plan above to unlock credit
                  purchasing.
                </Text>
              </CardBody>
            </Card>
          )}

          <Box mt={10}>
            <Heading size="md" mb={6}>
              Transaction History
            </Heading>
            {billingHistory.length === 0 ? (
              <Card>
                <CardBody textAlign="center" py={8}>
                  <Text color="gray.600">No transactions found.</Text>
                </CardBody>
              </Card>
            ) : (
              <TableContainer>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Date</Th>
                      <Th>Description</Th>
                      <Th>Amount</Th>
                      <Th>Status</Th>
                      <Th>Type</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {billingHistory.map((item) => (
                      <Tr key={item.id}>
                        <Td>
                          {new Date(item.created_at).toLocaleDateString()}
                        </Td>
                        <Td>
                          {item.description ||
                            item.type?.replace("_", " ").toUpperCase()}
                        </Td>
                        <Td>{formatAmount(item.amount)}</Td>
                        <Td>{item.status}</Td>
                        <Td>{item.type?.replace("_", " ").toUpperCase()}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {/* Confirmation Dialog */}
          <ConfirmationDialog
            isOpen={showCancelDialog}
            onClose={() => setShowCancelDialog(false)}
            onConfirm={confirmCancelSubscription}
            title="Cancel Subscription"
            description="Are you sure you want to cancel your subscription? It will remain active until the end of your current billing period."
            confirmText="Yes, Cancel Subscription"
            cancelText="No, Keep Subscription"
            isLoading={isProcessing}
            variant="destructive"
          />
        </VStack>
      </Container>
    </Box>
  );
}
